/**
 * The primitives every screen is built from.
 *
 * Deliberately unstyled-by-prop: each one renders the class names in
 * `styles.css` and nothing else, so a change to the look happens in one file
 * rather than in forty `style={{...}}` objects.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import {
  AlertIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  CopyIcon,
  InfoIcon,
  MinusIcon,
  SearchIcon,
  XIcon,
} from './icons.js'

// ── Page furniture ──────────────────────────────────────────────────────────

export function Crumbs({ trail }: { trail: Array<{ label: string; to?: string }> }): ReactNode {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {trail.map((crumb, index) => (
        <span key={crumb.label} style={{ display: 'contents' }}>
          {index > 0 && <ChevronRightIcon size={13} />}
          {crumb.to ? <a href={crumb.to}>{crumb.label}</a> : <strong>{crumb.label}</strong>}
        </span>
      ))}
    </nav>
  )
}

export function PageHead({
  title,
  subtitle,
  crumbs,
  actions,
}: {
  title: string
  subtitle?: string
  crumbs?: Array<{ label: string; to?: string }>
  actions?: ReactNode
}): ReactNode {
  return (
    <header className="page-head">
      <div className="page-head__text">
        {crumbs && <Crumbs trail={crumbs} />}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </header>
  )
}

export function Card({
  title,
  subtitle,
  action,
  flush,
  accent,
  footer,
  className,
  children,
}: {
  title?: ReactNode
  subtitle?: string
  action?: ReactNode
  flush?: boolean
  accent?: boolean
  footer?: ReactNode
  className?: string
  children: ReactNode
}): ReactNode {
  return (
    <section className={['card', accent ? 'card--accent' : '', className ?? ''].filter(Boolean).join(' ')}>
      {(title ?? action) && (
        <div className="card__head">
          <div>
            {typeof title === 'string' ? <h2>{title}</h2> : title}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {action && (
            <>
              <span className="spacer" />
              {action}
            </>
          )}
        </div>
      )}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
      {footer && <div className="card__foot">{footer}</div>}
    </section>
  )
}

// ── Status ──────────────────────────────────────────────────────────────────

/** `tone` doubles as the modifier, so an API status string styles itself. */
export function Badge({ tone, dot, children }: { tone?: string; dot?: boolean; children: ReactNode }): ReactNode {
  return (
    <span className={tone ? `badge badge--${tone}` : 'badge'}>
      {dot && <span className="badge__dot" />}
      {children}
    </span>
  )
}

export function Notice({
  kind = 'info',
  title,
  children,
}: {
  kind?: 'info' | 'ok' | 'warn' | 'error'
  title?: string
  children?: ReactNode
}): ReactNode {
  const Icon = kind === 'ok' ? CheckCircleIcon : kind === 'error' || kind === 'warn' ? AlertIcon : InfoIcon
  return (
    <div className={`notice notice--${kind}`} role={kind === 'error' ? 'alert' : undefined}>
      <Icon size={17} />
      <div className="notice__body">
        {title && <strong>{title}</strong>}
        {children}
      </div>
    </div>
  )
}

export function Delta({ value, upIsGood = true, suffix }: { value: number | null; upIsGood?: boolean; suffix?: string }): ReactNode {
  if (value === null) return <span className="delta is-flat">no earlier data</span>
  if (value === 0)
    return (
      <span className="delta is-flat">
        <MinusIcon size={13} />
        0%{suffix ? ` ${suffix}` : ''}
      </span>
    )

  const good = value > 0 === upIsGood
  const Arrow = value > 0 ? ArrowUpIcon : ArrowDownIcon
  return (
    <span className={`delta ${good ? 'is-good' : 'is-bad'}`}>
      <Arrow size={13} />
      {Math.abs(value)}%{suffix ? ` ${suffix}` : ''}
    </span>
  )
}

export function Stat({
  label,
  value,
  icon,
  tone = 'var(--brand)',
  delta,
  upIsGood = true,
  footnote,
}: {
  label: string
  value: ReactNode
  icon?: ReactNode
  tone?: string
  delta?: number | null
  upIsGood?: boolean
  footnote?: ReactNode
}): ReactNode {
  return (
    <article className="stat" style={{ ['--tone' as string]: tone }}>
      {icon && <span className="stat__icon">{icon}</span>}
      <div className="stat__body">
        <p className="stat__label">{label}</p>
        <p className="stat__value">{value}</p>
        {(delta !== undefined || footnote) && (
          <p className="stat__foot">
            {delta !== undefined && <Delta value={delta ?? null} upIsGood={upIsGood} />}
            {footnote}
          </p>
        )}
      </div>
    </article>
  )
}

// ── States ──────────────────────────────────────────────────────────────────

export function Empty({
  icon,
  title,
  children,
  action,
  error,
}: {
  icon?: ReactNode
  title: string
  children?: ReactNode
  action?: ReactNode
  error?: boolean
}): ReactNode {
  return (
    <div className={error ? 'empty empty--error' : 'empty'}>
      {icon && <span className="empty__icon">{icon}</span>}
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {action}
    </div>
  )
}

/** Shaped like the thing it replaces, so the layout does not jump on arrival. */
export function StatsSkeleton({ count = 5 }: { count?: number }): ReactNode {
  return (
    <div className="grid grid--4">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton skeleton--card" />
      ))}
    </div>
  )
}

export function RowsSkeleton({ rows = 6 }: { rows?: number }): ReactNode {
  return (
    <div className="card__body stack--sm">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton skeleton--text" style={{ width: `${100 - (i % 3) * 12}%` }} />
      ))}
    </div>
  )
}

// ── Tabs ────────────────────────────────────────────────────────────────────

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ key: T; label: string; count?: number }>
  value: T
  onChange: (key: T) => void
}): ReactNode {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={tab.key === value}
          className={tab.key === value ? 'tabs__tab is-active' : 'tabs__tab'}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.count !== undefined && <Badge tone={tab.key === value ? 'brand' : undefined}>{tab.count}</Badge>}
        </button>
      ))}
    </div>
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ key: T; label: string }>
  value: T
  onChange: (key: T) => void
  label: string
}): ReactNode {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={option.key === value ? 'is-active' : undefined}
          onClick={() => onChange(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

// ── Forms ───────────────────────────────────────────────────────────────────

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}): ReactNode {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  )
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  disabled?: boolean
}): ReactNode {
  return (
    <span className="switch">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="switch__track" />
      <span className="switch__thumb" />
    </span>
  )
}

export function SettingRow({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}): ReactNode {
  return (
    <div className="setting-row">
      <div className="setting-row__text">
        <strong>{title}</strong>
        {description && <small>{description}</small>}
      </div>
      {children}
    </div>
  )
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string
}): ReactNode {
  return (
    <div className="input-group toolbar__search">
      <SearchIcon size={16} />
      <input
        type="search"
        className="input"
        value={value}
        placeholder={placeholder ?? 'Search…'}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

// ── Menu ────────────────────────────────────────────────────────────────────

/**
 * Closes on outside click and on Escape.
 *
 * `mousedown` rather than `click`: a click listener fires after the trigger's
 * own handler has already re-opened the panel, which reads as "the menu will
 * not close".
 */
export function Menu({
  trigger,
  children,
  align = 'right',
  wide,
  bordered,
  label,
  onOpenChange,
}: {
  trigger: ReactNode
  children: ReactNode | ((close: () => void) => ReactNode)
  align?: 'left' | 'right'
  wide?: boolean
  bordered?: boolean
  label: string
  /**
   * Called when the panel opens and again when it closes — never on mount.
   *
   * There are four ways this thing closes (the trigger, a click outside,
   * Escape, and an item calling `close`), so a caller that needs to know has to
   * be told from one place rather than wrapped around each of them. The
   * notifications menu marks its feed read on the way *out*, which is why this
   * reports the direction rather than just firing.
   */
  onOpenChange?: (open: boolean) => void
}): ReactNode {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])

  // Held in a ref so a caller passing an inline arrow does not re-run the
  // effect below on every render — which would report "closed" forever.
  const notify = useRef(onOpenChange)
  notify.current = onOpenChange

  const mounted = useRef(false)

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    notify.current?.(open)
  }, [open])

  useEffect(() => {
    if (!open) return undefined

    function onPointer(event: globalThis.MouseEvent): void {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="menu" ref={root}>
      <button
        type="button"
        className={bordered ? 'menu__trigger menu__trigger--bordered' : 'menu__trigger'}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
      >
        {trigger}
        <ChevronDownIcon size={15} style={{ color: 'var(--ink-faint)' }} />
      </button>

      {open && (
        <div
          className={['menu__panel', align === 'left' ? 'menu__panel--left' : '', wide ? 'menu__panel--wide' : '']
            .filter(Boolean)
            .join(' ')}
          role="menu"
        >
          {typeof children === 'function' ? children(close) : children}
        </div>
      )}
    </div>
  )
}

export function MenuItem({
  icon,
  onClick,
  active,
  danger,
  children,
}: {
  icon?: ReactNode
  onClick?: () => void
  active?: boolean
  danger?: boolean
  children: ReactNode
}): ReactNode {
  return (
    <button
      type="button"
      role="menuitem"
      className={['menu__item', active ? 'is-active' : '', danger ? 'menu__item--danger' : ''].filter(Boolean).join(' ')}
      onClick={onClick}
    >
      {icon}
      <span className="spacer" style={{ textAlign: 'left' }}>
        {children}
      </span>
      {active && <CheckIcon size={15} />}
    </button>
  )
}

// ── Drawer ──────────────────────────────────────────────────────────────────

/**
 * Portalled to `body` so an overflow-hidden table cell cannot clip it, with
 * Escape wired up and page scroll frozen while it is open.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  footer,
  wide,
  children,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
  /** For drawers that hold a form or a two-column layout rather than a summary. */
  wide?: boolean
  children: ReactNode
}): ReactNode {
  const headingId = useId()

  useEffect(() => {
    if (!open) return undefined

    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose()
    }

    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)

    return () => {
      document.body.style.overflow = previous
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <>
      <button type="button" className="drawer-backdrop" aria-label="Close panel" onClick={onClose} />
      <aside className={wide ? 'drawer drawer--wide' : 'drawer'} role="dialog" aria-modal="true" aria-labelledby={headingId}>
        <header className="drawer__head">
          <div style={{ flex: 1, minWidth: 0 }}>
            {typeof title === 'string' ? <h2 id={headingId}>{title}</h2> : <div id={headingId}>{title}</div>}
            {subtitle && <p className="muted" style={{ fontSize: '.82rem' }}>{subtitle}</p>}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <XIcon size={18} />
          </button>
        </header>
        <div className="drawer__body">{children}</div>
        {footer && <div className="drawer__foot">{footer}</div>}
      </aside>
    </>,
    document.body,
  )
}

export function DrawerSection({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }): ReactNode {
  return (
    <section className="drawer__section">
      <div className="row">
        <h3 style={{ flex: 1 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

// ── Steps ───────────────────────────────────────────────────────────────────

export function Stepper({ steps, current }: { steps: string[]; current: number }): ReactNode {
  return (
    <ol className="stepper">
      {steps.map((step, index) => {
        const state = index < current ? 'is-done' : index === current ? 'is-current' : ''
        return (
          <li key={step} style={{ display: 'contents' }}>
            <div className={`stepper__step ${state}`}>
              <span className="stepper__num">{index < current ? <CheckIcon size={14} /> : index + 1}</span>
              <span className="stepper__label">{step}</span>
            </div>
            {index < steps.length - 1 && <span className="stepper__line" />}
          </li>
        )
      })}
    </ol>
  )
}

export function StepList({
  steps,
  current,
  onSelect,
}: {
  steps: Array<{ title: string; description: string }>
  current: number
  onSelect?: (index: number) => void
}): ReactNode {
  return (
    <div className="steplist">
      {steps.map((step, index) => (
        <button
          key={step.title}
          type="button"
          className={index === current ? 'steplist__item is-current' : 'steplist__item'}
          onClick={() => onSelect?.(index)}
        >
          <span className="stepper__num" style={index < current ? { background: 'color-mix(in srgb, var(--ok) 15%, var(--surface))', color: 'var(--ok)' } : undefined}>
            {index < current ? <CheckIcon size={14} /> : index + 1}
          </span>
          <span>
            <strong>{step.title}</strong>
            <small>{step.description}</small>
          </span>
        </button>
      ))}
    </div>
  )
}

export function Timeline({
  events,
}: {
  events: Array<{ label: string; at: string | null; state?: 'done' | 'current' | 'todo' }>
}): ReactNode {
  return (
    <ol className="timeline">
      {events.map((event) => (
        <li key={event.label} className={event.state === 'done' ? 'is-done' : event.state === 'current' ? 'is-current' : ''}>
          <span className="timeline__dot" />
          <span className="timeline__text">
            <strong>{event.label}</strong>
            <small>{event.at ?? '—'}</small>
          </span>
        </li>
      ))}
    </ol>
  )
}

// ── Pagination ──────────────────────────────────────────────────────────────

/**
 * Windowed page numbers with ellipses, because 129 pages of orders is a real
 * number here and rendering 129 buttons is not.
 */
export function Pager({
  page,
  pageCount,
  total,
  perPage,
  onPage,
  onPerPage,
  noun = 'rows',
  sizes = [10, 25, 50, 100],
}: {
  page: number
  pageCount: number
  total: number
  perPage: number
  onPage: (next: number) => void
  onPerPage?: (next: number) => void
  noun?: string
  /** The page sizes offered when `onPerPage` is set. */
  sizes?: number[]
}): ReactNode {
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  return (
    <div className="pager">
      <span>
        Showing {from.toLocaleString('en-IN')} to {to.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')} {noun}
      </span>

      <div className="pager__pages">
        <button type="button" className="pager__page" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
          <ChevronLeftIcon size={15} />
        </button>

        {pageWindow(page, pageCount).map((entry, index) =>
          entry === null ? (
            <span key={`gap-${index}`} className="pager__gap">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              className={entry === page ? 'pager__page is-active' : 'pager__page'}
              aria-current={entry === page ? 'page' : undefined}
              onClick={() => onPage(entry)}
            >
              {entry}
            </button>
          ),
        )}

        <button
          type="button"
          className="pager__page"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={15} />
        </button>
      </div>

      {onPerPage && (
        <select className="select" style={{ width: 'auto' }} value={perPage} onChange={(e) => onPerPage(Number(e.target.value))} aria-label="Rows per page">
          {sizes.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

function pageWindow(page: number, count: number): Array<number | null> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1)

  const pages = new Set([1, count, page, page - 1, page + 1])
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p))
  if (page >= count - 2) [count - 3, count - 2, count - 1].forEach((p) => pages.add(p))

  const sorted = [...pages].filter((p) => p >= 1 && p <= count).sort((a, b) => a - b)
  const out: Array<number | null> = []

  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1]! > 1) out.push(null)
    out.push(value)
  })

  return out
}

// ── Misc ────────────────────────────────────────────────────────────────────

export function KeyValue({ rows }: { rows: Array<{ label: string; value: ReactNode; kind?: 'total' | 'credit' | 'debit' }> }): ReactNode {
  return (
    <dl className="kv">
      {rows.map((row) => (
        <div key={row.label} className={row.kind ? `kv__row kv__row--${row.kind}` : 'kv__row'}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function Ticks({ items, brand }: { items: string[]; brand?: boolean }): ReactNode {
  return (
    <ul className={brand ? 'ticks ticks--brand' : 'ticks'}>
      {items.map((item) => (
        <li key={item}>
          <CheckIcon size={15} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export function Feature({ icon, title, description, tone }: { icon: ReactNode; title: string; description: string; tone?: string }): ReactNode {
  return (
    <div className="feature" style={tone ? { ['--tone' as string]: tone } : undefined}>
      <span className="feature__icon">{icon}</span>
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </div>
  )
}

/** Copies to the clipboard and says so for two seconds. */
export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }): ReactNode {
  const [done, setDone] = useState(false)

  async function copy(event: ReactMouseEvent): Promise<void> {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      setDone(true)
      setTimeout(() => setDone(false), 2000)
    } catch {
      // Clipboard access is denied over plain http and in some embedded
      // contexts. Silently doing nothing is better than an alert() here.
    }
  }

  return (
    <button type="button" className="icon-btn" onClick={(event) => void copy(event)} aria-label={done ? 'Copied' : label} title={done ? 'Copied' : label}>
      {done ? <CheckIcon size={15} style={{ color: 'var(--ok)' }} /> : <CopyIcon size={15} />}
    </button>
  )
}
